/**
 * AI Chat Mention System - Page Mention Service
 *
 * Handles # mentions for page references.
 * Provides autocomplete suggestions from the editor store's page list.
 */

import { useEditorStore } from '@/stores/editor/editor-store';
import type { Page } from '@/types';
import type {
  MentionContext,
  MentionSegment,
  MentionService,
  ResolvedMentionData,
  CacheEntry,
} from './types';
import { MAX_MENTION_SUGGESTIONS, PAGE_ICON, DEFAULT_PAGE_ICON } from './types';
import { getRecentMentions } from './RecentMentions';

// Shorter cache TTL for pages since they come from local store
const PAGE_CACHE_TTL = 10 * 1000; // 10 seconds

/**
 * Service for handling # page mentions
 *
 * Structure:
 * - #PageName - Reference to a page by name
 * - No hierarchical structure (pages are flat)
 */
export class PageMentionService implements MentionService {
  // Cache for page list
  private pageCache: CacheEntry<Page[]> | null = null;

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    if (!this.pageCache) return false;
    return Date.now() - this.pageCache.timestamp < PAGE_CACHE_TTL;
  }

  /**
   * Get pages from editor store with caching
   */
  private getPages(): Page[] {
    if (this.isCacheValid() && this.pageCache) {
      return this.pageCache.data;
    }

    const editorState = useEditorStore.getState();
    const pages = editorState.pages || [];
    const currentPageId = editorState.currentPageId;

    // Filter out current page (don't suggest current page)
    const filteredPages = pages.filter((page) => page.id !== currentPageId);

    // Sort: default page first, then alphabetically
    const sortedPages = [...filteredPages].sort((a, b) => {
      // Default page always first
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      // Then alphabetically by name
      return a.name.localeCompare(b.name);
    });

    this.pageCache = {
      data: sortedPages,
      timestamp: Date.now(),
    };

    return sortedPages;
  }

  /**
   * Get suggestions based on mention context
   * When no search term is provided, prepends recent page mentions
   */
  public async getSuggestions(context: MentionContext): Promise<MentionSegment[]> {
    // Pages don't have hierarchy, so path should be empty
    if (context.path.length > 0) {
      return [];
    }

    const pages = this.getPages();
    const suggestions: MentionSegment[] = [];
    const lowerSearch = context.searchTerm.toLowerCase();

    for (const page of pages) {
      // Filter by search term if provided
      if (!context.searchTerm || page.name.toLowerCase().includes(lowerSearch)) {
        suggestions.push({
          value: page.name,
          reference: page.name.includes(' ') ? `#"${page.name}"` : `#${page.name}`,
          id: page.id,
          description: page.description || (page.isDefault ? 'Default page' : 'Project page'),
          icon: page.isDefault ? DEFAULT_PAGE_ICON : PAGE_ICON,
          category: 'page',
          hasChildren: false, // Pages are leaf nodes
          metadata: {
            pageId: page.id,
            isDefaultPage: page.isDefault,
          },
        });
      }
    }

    // If no search term, prepend recent page mentions
    if (!context.searchTerm) {
      const recentMentions = getRecentMentions('page');
      if (recentMentions.length > 0) {
        // Remove duplicates (items that are in recent)
        const recentIds = new Set(recentMentions.map((m) => m.id));
        const filteredFresh = suggestions.filter((s) => !recentIds.has(s.id));

        return [
          ...recentMentions.map((m) => ({ ...m, category: 'Recent' as const })),
          ...filteredFresh,
        ].slice(0, MAX_MENTION_SUGGESTIONS);
      }
    }

    // Already sorted from getPages(), just apply limit
    return suggestions.slice(0, MAX_MENTION_SUGGESTIONS);
  }

  /**
   * Resolve a page mention reference to full data
   *
   * @param reference - The reference without # (e.g., "Dashboard")
   */
  public async resolveReference(reference: string): Promise<ResolvedMentionData | null> {
    // Get fresh page data (not from cache to ensure accuracy for resolution)
    const editorState = useEditorStore.getState();
    const pages = editorState.pages || [];

    const lowerReference = reference.toLowerCase();

    const page = pages.find((p) => p.name.toLowerCase() === lowerReference);

    if (!page) {
      return null;
    }

    return {
      type: 'page',
      reference,
      page: {
        id: page.id,
        name: page.name,
        description: page.description,
        isDefault: page.isDefault,
      },
    };
  }

  /**
   * Clear cache
   */
  public clearCache(): void {
    this.pageCache = null;
  }
}

// Export singleton instance
export const pageMentionService = new PageMentionService();
